import type { UseFetchOptions } from '#app'
import type { MaybeRefOrGetter } from 'vue'
import type { ApiOptionals, ApiResponse } from '~/types/api.type'
import type { Client } from '~/types/client.type'
import type { MappedOmit } from '~/types/common.type'
import type { CourseCategory } from '~/types/courseCategory.type'
import type { User } from '~/types/user.type'
import { defu } from 'defu'

export interface ApiUserGetListParams {
  clientId?: number
  projectId?: number
  onlyManaged?: boolean
  additionalUsers?: User['id'][]
  withCurrentParameters?: boolean
  withVacations?: boolean
  withHolidays?: boolean
  searchTerm?: string
  filterCourseCategories?: Array<CourseCategory['id']>
  withCourseCategories?: boolean
  withTrashed?: boolean
}

export interface ApiUserGetByIdParams {
  withCurrentParameters?: boolean
  withProjects?: boolean
  withManagedProjects?: boolean
  withSkills?: boolean
  withLatestWinningMeme?: boolean
}

export type ApiUserCreateUser = MappedOmit<User, 'id'> & {
  users: User['id'][]
  clientId: Client['id']
  leads: User['id'][]
  notificationThresholds: unknown[]
}

export function useApiUserGetList<T extends ApiUserGetListParams>(params: MaybeRefOrGetter<T> = {} as T, options: UseFetchOptions<ApiResponse<User<ApiOptionals<T>>[]>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<User<ApiOptionals<T>>[]>> = { method: 'GET', query: params }
  return useApiFetch('/users', defu(notOverridableOptions, options))
}

export function useApiUserGetById<T extends ApiUserGetByIdParams>(id: MaybeRefOrGetter<User['id']>, params: MaybeRef<T> = {} as T, options: UseFetchOptions<ApiResponse<User<ApiOptionals<T>>>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<User<ApiOptionals<T>>>> = { method: 'GET', query: params }
  return useApiFetch(() => `/users/${toValue(id)}`, defu(notOverridableOptions, options))
}

export function useApiUserCreate(user: MaybeRefOrGetter<ApiUserCreateUser>, options: UseFetchOptions<ApiResponse<User>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<User>> = { method: 'POST', body: toValue(user) }
  const overridableOptions: UseFetchOptions<ApiResponse<User>> = { immediate: false, watch: false }
  return useApiFetch('/users', defu(notOverridableOptions, options, overridableOptions))
}

export function useApiUserUpdateById(id: MaybeRefOrGetter<User['id']>, user: MaybeRefOrGetter<ApiUserCreateUser>, options: UseFetchOptions<ApiResponse<User>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<User>> = { method: 'PATCH', body: user }
  const overridableOptions: UseFetchOptions<ApiResponse<User>> = { immediate: false, watch: false }
  return useApiFetch(`/users/${toValue(id)}`, defu(notOverridableOptions, options, overridableOptions))
}

export function useApiUserDeleteById(id: MaybeRefOrGetter<User['id']>, options: UseFetchOptions<never> = {}) {
  const notOverridableOptions: UseFetchOptions<never> = { method: 'DELETE' }
  const overridableOptions: UseFetchOptions<never> = { immediate: false, watch: false }
  return useApiFetch(`/users/${toValue(id)}`, defu(notOverridableOptions, options, overridableOptions))
}
